import React, { useState } from 'react';
import { Button, Modal, Form, Input, DatePicker, Table, Space, Tag, Switch, Typography, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../contexts/ToastContext';
import apiService from '../../../services/api';
import { menteeProfileQueryKeys } from '../../../lib/queryClient';
import { MenteeEducationResponse, CreateMenteeEducationRequest, UpdateMenteeEducationRequest } from '../../../types';
import PanelTabCard from '../../../components/Common/PanelTabCard';
import TabListLoading from '../../../components/Common/TabListLoading';

const { Title } = Typography;

const MenteeEducationTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEducation, setEditingEducation] = useState<MenteeEducationResponse | null>(null);
  const [form] = Form.useForm();

  const { data: educations = [], isLoading: loading } = useQuery({
    queryKey: menteeProfileQueryKeys.educations,
    queryFn: async () => {
      const res = await apiService.getMenteeEducations();
      if (!res.success) throw new Error(res.message || 'Eğitimler yüklenemedi.');
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateMenteeEducationRequest) => apiService.createMenteeEducation(data),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Eğitim başarıyla eklendi.');
        queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.educations });
        setModalVisible(false);
        form.resetFields();
      } else toast.error(res.message || 'Eğitim eklenemedi.');
    },
    onError: () => toast.error('Eğitimler yüklenirken hata oluştu.'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateMenteeEducationRequest }) =>
      apiService.updateMenteeEducation(id, data),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Eğitim başarıyla güncellendi.');
        queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.educations });
        setModalVisible(false);
        setEditingEducation(null);
        form.resetFields();
      } else toast.error(res.message || 'Eğitim güncellenemedi.');
    },
    onError: () => toast.error('Eğitim güncellenirken hata oluştu.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteMenteeEducation(id),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Eğitim başarıyla silindi.');
        queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.educations });
      } else toast.error(res.message || 'Eğitim silinemedi.');
    },
    onError: () => toast.error('Eğitim silinirken hata oluştu.'),
  });

  const handleAdd = () => {
    setEditingEducation(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (education: MenteeEducationResponse) => {
    setEditingEducation(education);
    form.setFieldsValue({
      schoolName: education.schoolName,
      department: education.department,
      degree: education.degree,
      startDate: education.startDate ? dayjs(education.startDate) : null,
      endDate: education.endDate ? dayjs(education.endDate) : null,
      isGraduated: education.isGraduated,
      description: education.description
    });
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const educationData = {
        schoolName: values.schoolName,
        department: values.department,
        degree: values.degree,
        startDate: values.startDate.format('YYYY-MM-DD'),
        endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : null,
        isGraduated: values.isGraduated,
        description: values.description
      };

      if (editingEducation) {
        updateMutation.mutate({ id: editingEducation.id, data: educationData });
      } else {
        createMutation.mutate(educationData);
      }
    } catch {
      // form validation
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingEducation(null);
    form.resetFields();
  };

  const handleDelete = (id: number) => deleteMutation.mutate(id);

  // Filtreleme
  const filteredEducations = educations.filter(education =>
    education.schoolName.toLowerCase().includes(searchText.toLowerCase()) ||
    education.department.toLowerCase().includes(searchText.toLowerCase()) ||
    (education.degree && education.degree.toLowerCase().includes(searchText.toLowerCase()))
  );

  const columns = [
    {
      title: 'Okul Adı',
      dataIndex: 'schoolName',
      key: 'schoolName',
      sorter: (a: MenteeEducationResponse, b: MenteeEducationResponse) => a.schoolName.localeCompare(b.schoolName),
    },
    {
      title: 'Bölüm',
      dataIndex: 'department',
      key: 'department',
      sorter: (a: MenteeEducationResponse, b: MenteeEducationResponse) => a.department.localeCompare(b.department),
    },
    {
      title: 'Derece',
      dataIndex: 'degree',
      key: 'degree',
      render: (degree: string) => degree || '-',
    },
    {
      title: 'Başlangıç',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (date: string) => dayjs(date).format('MM/YYYY'),
      sorter: (a: MenteeEducationResponse, b: MenteeEducationResponse) => 
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
    },
    {
      title: 'Bitiş',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (date: string) => date ? dayjs(date).format('MM/YYYY') : '-',
    },
    {
      title: 'Durum',
      dataIndex: 'isGraduated',
      key: 'isGraduated',
      render: (isGraduated: boolean) => (
        <Tag color={isGraduated ? 'green' : 'blue'}>
          {isGraduated ? 'Mezun' : 'Devam Ediyor'}
        </Tag>
      ),
      filters: [
        { text: 'Mezun', value: true },
        { text: 'Devam Ediyor', value: false }
      ],
      onFilter: (value: boolean | React.Key, record: MenteeEducationResponse) => record.isGraduated === value,
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: MenteeEducationResponse) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ color: 'var(--text-secondary)' }}
          >
            Düzenle
          </Button>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            Sil
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
    <PanelTabCard
      title="Eğitim Bilgileri"
      description="Eğitim geçmişinizi ekleyin ve güncelleyin."
    >
        <TabListLoading loading={loading}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              style={{
                backgroundColor: 'var(--text-secondary)',
                borderColor: 'var(--text-secondary)',
                fontWeight: 500
              }}
            >
              Yeni Eğitim
            </Button>
          </Col>
        </Row>
        
        <Row style={{ marginBottom: '16px' }}>
          <Col span={8}>
            <Input
              placeholder="Okul, bölüm veya derece ara..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ borderRadius: '6px' }}
            />
          </Col>
        </Row>

        <Table
          scroll={{ x: 'max-content' }}
          columns={columns}
          dataSource={filteredEducations}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} eğitim`
          }}
        />
        </TabListLoading>
    </PanelTabCard>

      <Modal
        title={editingEducation ? 'Eğitim Düzenle' : 'Yeni Eğitim'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={600}
        okText={editingEducation ? 'Güncelle' : 'Ekle'}
        cancelText="İptal"
        okButtonProps={{
          style: { backgroundColor: 'var(--text-secondary)', borderColor: 'var(--text-secondary)' }
        }}
      >
        <Form layout="vertical" form={form} requiredMark={false}>
          <Form.Item 
            name="schoolName" 
            label="Okul Adı" 
            rules={[{ required: true, message: 'Okul adı gerekli' }]}
          >
            <Input placeholder="Örn: İstanbul Teknik Üniversitesi" />
          </Form.Item>

          <Form.Item 
            name="department" 
            label="Bölüm" 
            rules={[{ required: true, message: 'Bölüm gerekli' }]}
          >
            <Input placeholder="Örn: Bilgisayar Mühendisliği" />
          </Form.Item>

          <Form.Item 
            name="degree" 
            label="Derece"
          >
            <Input placeholder="Örn: Lisans, Yüksek Lisans, Doktora" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                name="startDate" 
                label="Başlangıç Tarihi" 
                rules={[
                  { required: true, message: 'Başlangıç tarihi gerekli' },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const today = dayjs().startOf('month');
                      const selectedDate = dayjs(value).startOf('month');
                      if (selectedDate.isAfter(today)) {
                        return Promise.reject(new Error('Başlangıç tarihi bugünden sonra olamaz'));
                      }
                      const endDate = form.getFieldValue('endDate');
                      if (endDate && selectedDate.isAfter(dayjs(endDate).startOf('month'))) {
                        return Promise.reject(new Error('Başlangıç tarihi bitiş tarihinden sonra olamaz'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <DatePicker 
                  style={{ width: '100%' }} 
                  picker="month"
                  placeholder="Başlangıç"
                  disabledDate={(current) => current && current.isAfter(dayjs(), 'month')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                name="endDate" 
                label="Bitiş Tarihi"
                dependencies={['startDate']}
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const startDate = form.getFieldValue('startDate');
                      if (startDate && dayjs(value).startOf('month').isBefore(dayjs(startDate).startOf('month'))) {
                        return Promise.reject(new Error('Bitiş tarihi başlangıç tarihinden önce olamaz'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <DatePicker 
                  style={{ width: '100%' }} 
                  picker="month"
                  placeholder="Bitiş"
                  disabledDate={(current) => {
                    const startDate = form.getFieldValue('startDate');
                    if (startDate && current) {
                      return current.isBefore(dayjs(startDate), 'month');
                    }
                    return false;
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item 
            name="isGraduated" 
            label="Mezun" 
            valuePropName="checked" 
            initialValue={false}
            dependencies={['startDate']}
            rules={[
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const startDate = form.getFieldValue('startDate');
                  if (startDate && dayjs(startDate).isAfter(dayjs(), 'month')) {
                    return Promise.reject(new Error('Gelecekteki bir tarih için mezun durumu seçilemez'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Switch />
          </Form.Item>

          <Form.Item 
            name="description" 
            label="Açıklama"
          >
            <Input.TextArea 
              rows={4} 
              placeholder="Eğitim hakkında ek bilgiler..."
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default MenteeEducationTab;
